import React, { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useBills, useIncomes, usePayments } from "../lib/hooks";
import { formatMoney, monthRange, occurrencesInRange, todayIso } from "../lib/finance";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CalendarDays, Receipt, TrendingUp, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import AdSlot from "../components/AdSlot";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { usePaywall } from "../components/Paywall";

export default function LandingPage() {
  const { user } = useAuth();
  const currency = user?.prefs?.currency || "USD";
  const { data: bills = [] } = useBills();
  const { data: incomes = [] } = useIncomes();
  const { data: payments = [] } = usePayments();
  const paywall = usePaywall();
  const hasFeatures = !!user?.subscription?.has_features;

  const now = new Date();
  const { startIso, endIso } = monthRange(now.getFullYear(), now.getMonth());

  const stats = useMemo(() => {
    const start = new Date(startIso), end = new Date(endIso);
    const paidSet = new Set(payments.filter((p) => p.paid).map((p) => `${p.bill_id}_${p.occurrence_date}`));
    let totalIncome = 0, totalBills = 0, paidBills = 0, unpaidBills = 0, count = 0, paidCount = 0;
    for (const inc of incomes) {
      const occs = occurrencesInRange(inc.first_date, inc.frequency, start, end);
      totalIncome += occs.length * Number(inc.amount || 0);
    }
    for (const b of bills) {
      const occs = occurrencesInRange(b.first_due_date, b.frequency, start, end);
      count += occs.length;
      for (const d of occs) {
        const amt = Number(b.amount || 0);
        totalBills += amt;
        if (paidSet.has(`${b.bill_id}_${d}`)) { paidBills += amt; paidCount += 1; }
        else unpaidBills += amt;
      }
    }
    return { totalIncome, totalBills, paidBills, unpaidBills, net: totalIncome - totalBills, paidPct: count ? Math.round((paidCount / count) * 100) : 0 };
  }, [bills, incomes, payments, startIso, endIso]);

  const upcoming = useMemo(() => {
    const t0 = new Date(todayIso());
    const t7 = new Date(t0); t7.setDate(t7.getDate() + 7);
    const paidSet = new Set(payments.filter((p) => p.paid).map((p) => `${p.bill_id}_${p.occurrence_date}`));
    const rows = [];
    for (const b of bills) {
      const occs = occurrencesInRange(b.first_due_date, b.frequency, t0, t7);
      for (const d of occs) if (!paidSet.has(`${b.bill_id}_${d}`)) rows.push({ ...b, date: d });
    }
    return rows.sort((a, b) => a.date.localeCompare(b.date)).slice(0, 5);
  }, [bills, payments]);

  // 6-month cashflow forecast
  const forecast = useMemo(() => {
    const now = new Date();
    const out = [];
    let running = 0;
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const { start: s, end: e } = monthRange(d.getFullYear(), d.getMonth());
      let inc = 0, bill = 0;
      for (const it of incomes) inc += occurrencesInRange(it.first_date, it.frequency, s, e).length * Number(it.amount || 0);
      for (const b of bills) bill += occurrencesInRange(b.first_due_date, b.frequency, s, e).length * Number(b.amount || 0);
      running += (inc - bill);
      out.push({ month: d.toLocaleString("default", { month: "short" }), net: Math.round((inc - bill) * 100) / 100, cumulative: Math.round(running * 100) / 100 });
    }
    return out;
  }, [bills, incomes]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-10" data-testid="landing-page">
      {paywall.Modal}
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border-2 border-border bg-card p-8 sm:p-12">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[hsl(var(--payday-bg))]/70 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[hsl(var(--due-bg))]/60 blur-3xl pointer-events-none"/>
        <div className="absolute inset-x-8 top-3 h-px bg-border/60 pointer-events-none"/>
        <div className="absolute inset-x-8 top-5 h-px bg-border/40 pointer-events-none"/>
        <div className="relative">
          <Badge variant="outline" className="mb-4 gap-1 font-newspaper tracking-widest uppercase text-[10px]"><Sparkles size={12}/> The Ledger · {now.toLocaleString("default", { month: "long", year: "numeric" })}</Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl">
            Hey {user?.name?.split(" ")[0] || "friend"}, here's your <span className="italic text-[hsl(var(--primary))]">monthly</span> cashflow choreography.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-xl">
            Bills, paydays, and per-occurrence paid stamps — laid out on a calm calendar.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" data-testid="landing-cta-open-calendar">
              <Link to="/calendar">Open monthly ledger <ArrowRight size={16} className="ml-1"/></Link>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid="landing-cta-add-bill">
              <Link to="/bills">Add a bill</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Bento stats */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Income (month)" value={formatMoney(stats.totalIncome, currency)}
          icon={<TrendingUp size={16}/>} accent="text-[hsl(var(--paid-text))]" tid="stat-income"/>
        <StatCard title="Bills (month)" value={formatMoney(stats.totalBills, currency)}
          icon={<Receipt size={16}/>} accent="text-[hsl(var(--due-text))]" tid="stat-bills"/>
        <StatCard title="Net (safe to spend)" value={formatMoney(stats.net, currency)}
          icon={<CalendarDays size={16}/>} accent={stats.net >= 0 ? "text-[hsl(var(--paid-text))]" : "text-[hsl(var(--overdue-text))]"} tid="stat-net"/>
        <Card data-testid="stat-progress">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground font-medium flex items-center gap-2"><CheckCircle2 size={16}/> Payment velocity</CardTitle></CardHeader>
          <CardContent>
            <div className="tabular text-2xl font-bold">{stats.paidPct}%</div>
            <Progress value={stats.paidPct} className="mt-2"/>
            <div className="text-xs text-muted-foreground mt-2 tabular">{formatMoney(stats.paidBills, currency)} paid · {formatMoney(stats.unpaidBills, currency)} remaining</div>
          </CardContent>
        </Card>
      </section>

      {/* Forecast section — premium, teaser for free */}
      <section className="relative">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold">6-month cashflow forecast</h2>
          {!hasFeatures && <button className="text-xs uppercase tracking-widest text-[hsl(var(--primary))] font-bold hover:underline" onClick={()=>paywall.gate("Cashflow forecast")} data-testid="forecast-unlock">Unlock →</button>}
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 relative overflow-hidden">
          {!hasFeatures && (
            <div className="absolute inset-0 z-10 backdrop-blur-md bg-card/60 flex flex-col items-center justify-center gap-2" data-testid="forecast-locked">
              <div className="font-newspaper italic text-sm text-muted-foreground">Premium feature</div>
              <button className="text-sm font-bold text-[hsl(var(--primary))] underline" onClick={()=>paywall.gate("Cashflow forecast")}>See what your net looks like in 6 months →</button>
            </div>
          )}
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={forecast} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="currentColor" />
              <YAxis tick={{ fontSize: 11 }} stroke="currentColor" width={60} tickFormatter={(v)=>`${v}`}/>
              <Tooltip formatter={(v)=>formatMoney(v, currency)}/>
              <Line type="monotone" dataKey="cumulative" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="net" stroke="hsl(var(--payday-border))" strokeWidth={1.5} strokeDasharray="4 4" dot={false}/>
            </LineChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[hsl(var(--primary))]"/> Cumulative net</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[hsl(var(--payday-border))]"/> Monthly delta</span>
          </div>
        </div>
      </section>

      {/* Ad slot for non-premium */}
      <AdSlot slot="1234567890" className="my-2"/>

      {/* Upcoming */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold">Upcoming (next 7 days)</h2>
          <Link to="/paid" className="text-sm text-muted-foreground hover:text-foreground">Mark paid →</Link>
        </div>
        <div className="rounded-2xl border border-border bg-card divide-y">
          {upcoming.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground" data-testid="upcoming-empty">Nothing due in the next week. Breathe easy ☕.</div>
          )}
          {upcoming.map((u) => (
            <div key={`${u.bill_id}_${u.date}`} className="p-4 flex items-center justify-between" data-testid={`upcoming-row-${u.bill_id}-${u.date}`}>
              <div>
                <div className="font-semibold">{u.name}</div>
                <div className="text-xs text-muted-foreground">{u.category} · {new Date(u.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</div>
              </div>
              <div className="tabular font-bold text-[hsl(var(--due-text))]">{formatMoney(u.amount, currency)}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function StatCard({ title, value, icon, accent = "", tid }) {
  return (
    <Card data-testid={tid}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground font-medium flex items-center gap-2">{icon} {title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`tabular text-2xl font-bold ${accent}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
